import { Button } from "@/components/flexkit/Button";
import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from "@/components/podkit/dialog/Dialog";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { type Provider } from "@/components/runners/details/SourceControlProvider";
import { SourceControlProviderIcon } from "@/components/runners/details/SourceControlProviderIcon";
import { useSegmentTrack } from "@/hooks/use-segment";
import { type RunnerKind, type SCMIntegration } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { useCallback, useState, type FC } from "react";

export const SourceControlProviderAddModal: FC<{
    providers: Provider[];
    runnerKind: RunnerKind;
    onProviderCreate: (provider: Provider) => void;
    integrations?: SCMIntegration[];
}> = ({ providers, runnerKind, onProviderCreate: onProviderCreate, integrations = [] }) => {
    // Filter out Azure DevOps provider if there are no existing Azure DevOps integrations
    const hasExistingAzureDevOpsIntegration = integrations.some((integration) => integration.scmId === "azuredevops");

    const filteredProviders = providers.filter((provider) => {
        // If it's not Azure DevOps, always include it
        if (provider.schema.scmId !== "azuredevops") {
            return true;
        }

        // For Azure DevOps, only include if there are existing integrations
        return hasExistingAzureDevOpsIntegration;
    });

    return (
        <>
            <DialogHeader>
                <DialogTitle>Configure repository access</DialogTitle>
                <DialogDescription>
                    In order for your runner to access your repos, you&apos;ll need to configure your repository
                    provider (GitHub, GitLab, Bitbucket or Azure DevOps).
                </DialogDescription>
            </DialogHeader>

            <DialogBody>
                <div className="flex flex-col gap-2">
                    {filteredProviders.map((provider) => (
                        <ProviderRow
                            key={provider.schema.scmId}
                            provider={provider}
                            runnerKind={runnerKind}
                            onProviderCreate={onProviderCreate}
                            readOnly={false}
                        />
                    ))}
                </div>
            </DialogBody>
        </>
    );
};

export const ProviderRow: FC<{
    readOnly: boolean;
    provider: Provider;
    runnerKind: RunnerKind;
    onProviderCreate?: (provider: Provider) => void;
}> = ({ provider, runnerKind, onProviderCreate, readOnly }) => {
    return (
        <div className="flex w-full items-center justify-between rounded-xl border-0.5 border-border-base bg-surface-secondary px-3 py-2">
            <div className="flex items-center gap-3">
                <div className="flex size-8 items-center justify-center rounded-lg">
                    <SourceControlProviderIcon scmId={provider.schema.scmId} />
                </div>
                <Text className="text-base font-bold">{provider.name}</Text>
                {provider.schema.scmId === "azuredevops" && (
                    <Text className="text-sm text-content-secondary">(Deprecated)</Text>
                )}
            </div>
            <ProviderStatus
                readOnly={readOnly}
                provider={provider}
                runnerKind={runnerKind}
                onProviderCreate={onProviderCreate}
            />
        </div>
    );
};

const ProviderStatus: FC<{
    provider: Provider;
    runnerKind: RunnerKind;
    readOnly: boolean;
    onProviderCreate?: (provider: Provider) => void;
}> = ({ provider, runnerKind, onProviderCreate, readOnly }) => {
    const segmentTrack = useSegmentTrack();
    const [waitListJoined, setWaitListJoined] = useState(false);

    const onJoinWaitlist = useCallback(() => {
        segmentTrack("waitlist_joined", {
            feature: provider.analyticsName || provider.name,
            provider: provider.name,
            type: "git_provider",
            runner_kind: runnerKind,
        });
        setWaitListJoined(true);
    }, [segmentTrack, setWaitListJoined, provider, runnerKind]);

    const label = waitListJoined ? "Joined waitlist" : "Join waitlist";
    switch (provider.state) {
        case "coming-soon":
            return (
                <Button
                    variant="link"
                    size="md"
                    onClick={onJoinWaitlist}
                    disabled={waitListJoined}
                    className={cn(waitListJoined && "disabled:bg-transparent disabled:text-content-green")}
                    aria-label={`${label}: ${provider.name}`}
                    data-tracking-id="join-waitlist-scm-provider-add-modal"
                >
                    {label}
                </Button>
            );
        case "available":
            return (
                <Button
                    variant="secondary"
                    onClick={onProviderCreate ? () => onProviderCreate(provider) : undefined}
                    data-testid={`select-${provider.schema.scmId}`}
                    disabled={readOnly || provider.schema.scmId === "azuredevops" || !onProviderCreate}
                    aria-label={`Select SCM: ${provider.name}`}
                    data-tracking-id="select-scm-provider-scm-provider-add-modal"
                >
                    Select
                </Button>
            );
    }
};
